// --------------------------------
// Copyright (c) 2023 Eco8 S.r.l.
// --------------------------------
import React, { useState, useEffect } from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { Calendar, LocaleConfig } from "react-native-calendars";
import { MaterialIcons } from "@expo/vector-icons";
import colors from "../../../../../config/colors";
import styles from "../../../../../styles/Farmer/RangeDataPickerStyle";
import * as RNLocalize from "react-native-localize";
import { formatDate } from "../../../../../utils/utils";
import fonts from "../../../../../config/fonts";
const customLocaleConfig = {
    de: {
        monthNames: [
            "Januar",
            "Februar",
            "März",
            "April",
            "Mai",
            "Juni",
            "Juli",
            "August",
            "September",
            "Oktober",
            "November",
            "Dezember",
        ],
        monthNamesShort: [
            "Jan",
            "Feb",
            "Mär",
            "Apr",
            "Mai",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Okt",
            "Nov",
            "Dez",
        ],
        dayNames: [
            "Sonntag",
            "Montag",
            "Dienstag",
            "Mittwoch",
            "Donnerstag",
            "Freitag",
            "Samstag",
        ],
        dayNamesShort: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
        today: "Heute",
    },
    it: {
        monthNames: [
            "Gennaio",
            "Febbraio",
            "Marzo",
            "Aprile",
            "Maggio",
            "Giugno",
            "Luglio",
            "Agosto",
            "Settembre",
            "Ottobre",
            "Novembre",
            "Dicembre",
        ],
        monthNamesShort: [
            "Gen",
            "Feb",
            "Mar",
            "Apr",
            "Mag",
            "Giu",
            "Lug",
            "Ago",
            "Set",
            "Ott",
            "Nov",
            "Dic",
        ],
        dayNames: [
            "Domenica",
            "Lunedì",
            "Martedì",
            "Mercoledì",
            "Giovedì",
            "Venerdì",
            "Sabato",
        ],
        dayNamesShort: ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"],
        today: "Oggi",
    },
    en: {
        monthNames: [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ],
        monthNamesShort: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ],
        dayNames: [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
        ],
        dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        today: "Today",
    },
};

const CalendarScreen = ({ language, onDateRangeChange, getTranslation }) => {
    const [isCalendarVisible, setIsCalendarVisible] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState("");
    const [selectedEndDate, setSelectedEndDate] = useState("");

    const userPreferredLanguage =
        RNLocalize.getLocales()[0].languageTag.split("-")[0];

    useEffect(() => {
        const locale =
            customLocaleConfig[userPreferredLanguage] || customLocaleConfig["en"];
        LocaleConfig.locales[userPreferredLanguage] = locale;
        LocaleConfig.defaultLocale = userPreferredLanguage;
    }, [userPreferredLanguage]);

    const onDayPress = (day) => {
        if (selectedStartDate === "") {
            setSelectedStartDate(day.dateString);
        } else if (selectedEndDate === "") {
            if (day.dateString < selectedStartDate) {
                setSelectedStartDate(day.dateString);
                setSelectedEndDate(selectedStartDate);
            } else {
                setSelectedEndDate(day.dateString);
                setIsCalendarVisible(false);
                onDateRangeChange(selectedStartDate, day.dateString);
            }
        } else {
            setSelectedStartDate(day.dateString);
            setSelectedEndDate("");
        }
    };

    const renderCalendar = () => {
        const markedDates = {};
        if (selectedStartDate !== "") {
            markedDates[selectedStartDate] = {
                startingDay: true,
                color: colors.green,
                textColor: colors.white,
            };
        }
        if (selectedEndDate !== "") {
            markedDates[selectedEndDate] = {
                endingDay: true,
                color: colors.green,
                textColor: colors.white,
            };
            const start = new Date(selectedStartDate);
            const end = new Date(selectedEndDate);
            let currentDate = new Date(start);
            while (currentDate <= end) {
                markedDates[currentDate.toISOString().slice(0, 10)] = {
                    color: colors.green,
                    textColor: colors.white,
                };
                currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        return (
            <View style={styles.calendarContainer}>
                <Calendar
                    markingType={"period"}
                    markedDates={markedDates}
                    onDayPress={onDayPress}
                    renderArrow={(direction) => (
                        <MaterialIcons
                            name={direction === "left" ? "chevron-left" : "chevron-right"}
                            size={24}
                            color={colors.green}
                        />
                    )}
                    theme={{
                        todayTextColor: colors.green,
                        todayButtonFontFamily: fonts.regular,
                        monthTextColor: colors.black,
                        textMonthFontWeight: "bold",
                        textDayHeaderFontWeight: "bold",
                        textDayFontSize: 16,
                        textMonthFontSize: 20,
                    }}
                    maxDate={new Date().toISOString().split('T')[0]}
                />
            </View>
        );
    };

    return (
        <View style={styles.container}>
            <TouchableOpacity
                style={styles.buttonContainer}
                onPress={() => setIsCalendarVisible(!isCalendarVisible)}
            >
                <MaterialIcons name="date-range" size={24} color={colors.white} />
                <Text style={styles.buttonText}>
                    {selectedStartDate
                        ? `${formatDate(selectedStartDate)}${selectedEndDate ? ` - ${formatDate(selectedEndDate)}` : " -"
                        }`
                        : getTranslation("Seleziona un periodo")}
                </Text>
            </TouchableOpacity>
            {isCalendarVisible && renderCalendar()}
        </View>
    );
};

export default CalendarScreen;
